import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../hooks/useAuth';
import { fetchCountryList } from 'Api/countriesAPI';
import { buildPhoneCodes, EMPTY_PHONE_CODES, loadPhoneCodes } from 'helpers/phoneCodes';

export const CountriesContext = createContext(null);

/**
 * The country / dial-code list for every signed-in CRM screen — fetched
 * EXACTLY ONCE per login from `GET /app/v1/county` and held here.
 *
 * ── Why a provider and not a per-page fetch ─────────────────────────────────
 * Four screens read the same list (Create Partner, GST, Manual Flight, Manual
 * Hotel). Mounted inside AdminApp's ProtectedRoute, this fetches when the
 * console first renders after sign-in and the result outlives every route
 * change, so opening a screen never calls the API; logging out unmounts it,
 * so the next login fetches fresh. A full page reload also refetches, since
 * Context state does not survive one. Screens read it through
 * `usePhoneCodes()`, whose shape is unchanged.
 *
 * ── Why straight after login ────────────────────────────────────────────────
 * The route is a legacy GET called directly (see `fetchCountryList`), so it
 * has none of portalClient's refresh-on-401. Firing while the token is fresh
 * is what makes that safe.
 *
 * ── Failure: the bundled JSON, and ONLY then ────────────────────────────────
 * When the API answers, its list is the only one shown and the bundled
 * Phone_Codes.json is never even loaded. When it fails, the pickers fall back
 * to that JSON (its lazy `phone-codes` chunk, via `loadPhoneCodes`) so the
 * screens stay usable; `error` and `source: 'fallback'` say so, and
 * `fetchCountryList` has already logged the status and server message under
 * `[countries]`. Not retried — one call per login is the contract; a re-login
 * (or reload) tries the API again.
 */
export function CountriesProvider({ children }) {
  const { accessJWT } = useAuth();
  const [state, setState] = useState({ data: EMPTY_PHONE_CODES, loading: true, error: false, source: null });

  // Read through a ref so the effect never depends on the token: a rotation
  // re-renders this with a new accessJWT and must not refetch.
  const tokenRef = useRef(accessJWT);
  tokenRef.current = accessJWT;

  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    // StrictMode runs this effect twice on mount; one GET is the contract.
    if (!startedRef.current) {
      startedRef.current = true;
      fetchCountryList(tokenRef.current)
        .then((rows) => {
          if (rows) return { data: buildPhoneCodes(rows), loading: false, error: false, source: 'api' };
          // eslint-disable-next-line no-console
          console.warn('[countries] using the bundled Phone_Codes.json until the next login');
          // loadPhoneCodes never rejects — it settles on an empty list instead.
          return loadPhoneCodes().then((fallback) => ({
            data: fallback, loading: false, error: true, source: 'fallback',
          }));
        })
        .then((next) => {
          if (mountedRef.current) setState(next);
        });
    }
    return () => { mountedRef.current = false; };
  }, []);

  const value = useMemo(
    () => ({
      codes: state.data.codes,
      byIso: state.data.byIso,
      dialCodeFor: state.data.dialCodeFor,
      loading: state.loading,
      error: state.error,
      // 'api' | 'fallback' once settled, null while loading.
      source: state.source,
    }),
    [state],
  );

  return <CountriesContext.Provider value={value}>{children}</CountriesContext.Provider>;
}
